/**
 * Performance Context Provider
 * Global performance state management and metric collection
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
	getPerformanceSummary,
	subscribeToMetrics,
} from '../utils/performance';
import {
	getApiPerformanceSummary,
	subscribeToApiMetrics,
} from '../utils/apiMonitoring';

const PerformanceContext = createContext(null);

/**
 * Performance Context Provider Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.enabled - Whether performance monitoring is enabled
 */
export const PerformanceProvider = ({ children, enabled = false }) => {
	const [coreWebVitals, setCoreWebVitals] = useState({});
	const [apiMetrics, setApiMetrics] = useState(new Map());
	const [componentMetrics, setComponentMetrics] = useState(new Map());
	const [memoryMetrics, setMemoryMetrics] = useState(new Map());
	const [isEnabled, setIsEnabled] = useState(enabled);

	const metricsRef = useRef({
		coreWebVitals: {},
		apiMetrics: new Map(),
		componentMetrics: new Map(),
		memoryMetrics: new Map(),
	});

	// Subscribe to Core Web Vitals updates
	useEffect(() => {
		if (!isEnabled) return;

		const unsubscribe = subscribeToMetrics((metric) => {
			setCoreWebVitals((prev) => {
				const updated = { ...prev };
				if (!updated[metric.name]) {
					updated[metric.name] = [];
				}
				updated[metric.name].push({
					...metric,
					timestamp: Date.now(),
				});

				// Keep only last 100 metrics per type
				if (updated[metric.name].length > 100) {
					updated[metric.name].shift();
				}

				metricsRef.current.coreWebVitals = updated;
				return updated;
			});
		});

		return unsubscribe;
	}, [isEnabled]);

	// Subscribe to API metrics updates
	useEffect(() => {
		if (!isEnabled) return;

		const unsubscribe = subscribeToApiMetrics((apiMetric) => {
			setApiMetrics((prev) => {
				const updated = new Map(prev);
				const apiName = apiMetric.apiName || 'unknown';
				if (!updated.has(apiName)) {
					updated.set(apiName, []);
				}

				const metrics = updated.get(apiName);
				metrics.push({
					...apiMetric,
					timestamp: Date.now(),
				});

				if (metrics.length > 100) {
					metrics.shift();
				}

				metricsRef.current.apiMetrics = updated;
				return updated;
			});
		});

		return unsubscribe;
	}, [isEnabled]);

	/**
	 * Get performance summary
	 * @returns {Object} Performance summary
	 */
	const getSummary = useCallback(() => {
		return {
			coreWebVitals: getPerformanceSummary(),
			apiMetrics: getApiPerformanceSummary(),
			componentMetrics: Array.from(componentMetrics.entries()).reduce(
				(acc, [name, metrics]) => {
					acc[name] = {
						name,
						renderCount: metrics.length,
						averageRenderTime:
							metrics.reduce((sum, m) => sum + (m.renderTime || 0), 0) / metrics.length,
						lastRenderTime: metrics[metrics.length - 1]?.renderTime || 0,
					};
					return acc;
				},
				{}
			),
			memoryMetrics: Array.from(memoryMetrics.entries()).reduce((acc, [name, stats]) => {
				acc[name] = stats;
				return acc;
			}, {}),
		};
	}, [componentMetrics, memoryMetrics]);

	/**
	 * Update component metrics
	 * @param {string} componentName - Component name
	 * @param {Object} metric - Metric data
	 */
	const updateComponentMetrics = useCallback((componentName, metric) => {
		setComponentMetrics((prev) => {
			const updated = new Map(prev);
			if (!updated.has(componentName)) {
				updated.set(componentName, []);
			}

			const metrics = updated.get(componentName);
			metrics.push({
				...metric,
				timestamp: Date.now(),
			});

			if (metrics.length > 100) {
				metrics.shift();
			}

			metricsRef.current.componentMetrics = updated;
			return updated;
		});
	}, []);

	/**
	 * Update memory metrics
	 * @param {string} componentName - Component name
	 * @param {Object} stats - Memory statistics
	 */
	const updateMemoryMetrics = useCallback((componentName, stats) => {
		setMemoryMetrics((prev) => {
			const updated = new Map(prev);
			updated.set(componentName, {
				...stats,
				lastUpdate: Date.now(),
			});
			metricsRef.current.memoryMetrics = updated;
			return updated;
		});
	}, []);

	/**
	 * Clear all metrics
	 */
	const clearMetrics = useCallback(() => {
		setCoreWebVitals({});
		setApiMetrics(new Map());
		setComponentMetrics(new Map());
		setMemoryMetrics(new Map());
		metricsRef.current = {
			coreWebVitals: {},
			apiMetrics: new Map(),
			componentMetrics: new Map(),
			memoryMetrics: new Map(),
		};
	}, []);

	const value = {
		isEnabled,
		setIsEnabled,
		coreWebVitals,
		apiMetrics,
		componentMetrics,
		memoryMetrics,
		getSummary,
		updateComponentMetrics,
		updateMemoryMetrics,
		clearMetrics,
	};

	return (
		<PerformanceContext.Provider value={value}>
			{children}
		</PerformanceContext.Provider>
	);
};

/**
 * Hook to use Performance Context
 * @returns {Object} Performance context value
 */
export const usePerformance = () => {
	const context = useContext(PerformanceContext);
	if (!context) {
		// Return a no-op context if not within provider
		return {
			isEnabled: false,
			setIsEnabled: () => {},
			coreWebVitals: {},
			apiMetrics: new Map(),
			componentMetrics: new Map(),
			memoryMetrics: new Map(),
			getSummary: () => ({}),
			updateComponentMetrics: () => {},
			updateMemoryMetrics: () => {},
			clearMetrics: () => {},
		};
	}
	return context;
};

export default PerformanceContext;

